// @ts-check

/**
 * @file Renders task detail rows.
 */

import Text from "@kaspernj/api-maker/build/utils/text"
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import {memo} from "react"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native-propforge"

import DetailValue from "../../components/detail-value"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */

/** @type {Record<string, object>} */
const styles = {}

/**
 * @typedef {object} TaskDetailRowProps
 * @property {string} label Detail label.
 * @property {string} testID Stable test id suffix.
 * @property {string} value Detail value.
 */

/** @typedef {Record<string, never>} TaskDetailRowState */

/**
 * Static detail row for task show fields.
 * @augments {ShapeComponent<TaskDetailRowProps, TaskDetailRowState>}
 */
class TaskDetailRow extends ShapeComponent {
  static propTypes = PropTypesExact({
    label: PropTypes.string.isRequired,
    testID: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
  })

  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()

  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** Initializes theme values for the row. */
  setup() {
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders the detail row.
   * @returns {ReactNode} Label and value content.
   */
  render() {
    const {label, testID, value} = this.p
    const colors = this.tt.colors
    const themeName = this.tt.themeName

    return (
      <View
        collapsable={false}
        style={styles[`detailRow-${themeName}`] ||= {
          borderBottomColor: colors.divider,
          borderBottomWidth: 1,
          paddingVertical: 10
        }}
        testID={`taskShowScreen/${testID}/row`}
      >
        <Text
          style={styles[`detailLabel-${themeName}`] ||= {
            color: colors.textMuted,
            fontWeight: 600,
            marginBottom: 4
          }}
          testID={`taskShowScreen/${testID}/label`}
        >
          {label}
        </Text>
        <DetailValue
          style={styles[`detailValue-${themeName}`] ||= {
            fontSize: 16
          }}
          testID={`taskShowScreen/${testID}/value`}
          value={value}
        />
      </View>
    )
  }
}

/** @type {NamedExoticComponent<TaskDetailRowProps>} */
const TaskDetailRowComponent = memo(shapeComponent(TaskDetailRow))

export default TaskDetailRowComponent
